/**
 * Cookie / storage-technology consent state for the KleipData website.
 *
 * The KleipData Cookie Policy defines several technology categories. This
 * implementation is deliberately simplified — it does NOT ask for separate
 * consent for each type of data collected. It records two things:
 *
 *  - `optional`   : opt-in consent for technologies that require consent under
 *                   PECR (optional analytics, added functionality, advertising).
 *                   Default OFF. Nothing in these categories runs until this is true.
 *
 *  - `exceptions` : whether the visitor allows technologies that may lawfully run
 *                   under a PECR exception (statistical-purpose analytics and
 *                   appearance/preference storage). Default ON, but the visitor
 *                   has a simple, free means of objecting (setting this to false).
 *
 * Strictly necessary and security technologies are always permitted and are not
 * represented here.
 *
 * No third-party analytics or advertising technologies are currently loaded by
 * this site. Any future integration MUST call `readConsent()` and only activate
 * when the relevant flag allows it.
 */

export const CONSENT_STORAGE_KEY = "kleipdata_cookie_consent";
export const CONSENT_CONFIG_VERSION = 1;
export const CONSENT_EVENT = "kleipdata:cookie-consent";
export const OPEN_SETTINGS_EVENT = "kleipdata:open-cookie-settings";

export type CookieConsent = {
  /** consent-configuration version the choice was recorded against */
  v: number;
  /** whether the visitor has been shown, and made, a choice */
  presented: boolean;
  /** opt-in consent for consent-required technologies (default false) */
  optional: boolean;
  /** allow exception-based statistical & appearance technologies (default true) */
  exceptions: boolean;
  /** ISO timestamp of the recorded choice */
  ts: string;
};

export const DEFAULT_CONSENT: CookieConsent = {
  v: CONSENT_CONFIG_VERSION,
  presented: false,
  optional: false,
  exceptions: true,
  ts: "",
};

declare global {
  interface Window {
    kleipdataCookieConsent?: CookieConsent;
  }
}

function parseFromStorage(): CookieConsent {
  if (typeof window === "undefined") return DEFAULT_CONSENT;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) return DEFAULT_CONSENT;
    const parsed = JSON.parse(raw) as Partial<CookieConsent> | null;
    if (!parsed || typeof parsed !== "object") return DEFAULT_CONSENT;
    // A choice recorded against an older configuration is re-requested.
    if (parsed.v !== CONSENT_CONFIG_VERSION) return DEFAULT_CONSENT;
    return {
      v: CONSENT_CONFIG_VERSION,
      presented: parsed.presented === true,
      optional: parsed.optional === true,
      exceptions: parsed.exceptions !== false,
      ts: typeof parsed.ts === "string" ? parsed.ts : "",
    };
  } catch {
    return DEFAULT_CONSENT;
  }
}

/* ------------------------------------------- cached snapshot for React store */

let snapshot: CookieConsent = DEFAULT_CONSENT;
let loaded = false;

/** Stable snapshot for useSyncExternalStore (client). */
export function getConsentSnapshot(): CookieConsent {
  if (typeof window === "undefined") return DEFAULT_CONSENT;
  if (!loaded) {
    snapshot = parseFromStorage();
    loaded = true;
  }
  return snapshot;
}

/** Server snapshot — consent state is unknown until the client mounts. */
export function getServerConsentSnapshot(): CookieConsent {
  return DEFAULT_CONSENT;
}

export function subscribeConsent(callback: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  const handler = () => {
    snapshot = parseFromStorage();
    loaded = true;
    callback();
  };
  window.addEventListener(CONSENT_EVENT, handler);
  window.addEventListener("storage", handler);
  return () => {
    window.removeEventListener(CONSENT_EVENT, handler);
    window.removeEventListener("storage", handler);
  };
}

/** Fresh, uncached read — for use outside React. */
export function readConsent(): CookieConsent {
  return parseFromStorage();
}

export function writeConsent(choice: {
  optional: boolean;
  exceptions: boolean;
}): CookieConsent {
  const next: CookieConsent = {
    v: CONSENT_CONFIG_VERSION,
    presented: true,
    optional: choice.optional,
    exceptions: choice.exceptions,
    ts: new Date().toISOString(),
  };
  snapshot = next;
  loaded = true;
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* storage unavailable — the choice applies for this session only */
    }
    window.kleipdataCookieConsent = next;
    window.dispatchEvent(
      new CustomEvent<CookieConsent>(CONSENT_EVENT, { detail: next }),
    );
  }
  return next;
}

/** Opens the Cookie Settings dialog from anywhere (e.g. the footer link). */
export function openCookieSettings() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(OPEN_SETTINGS_EVENT));
}
